import { Constants } from '@/cfg/constants';

function partValue(parts, type) {
  const part = parts.find((p) => p.type === type);
  return part ? part.value : '';
}

// "Session began 5 August 2026 at 22:55 BST" (uk) / "Session began Aug 5, 2026 at 10:55 PM EDT" (global)
export function formatSessionBeganLabel(dateInput) {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '';

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isUk = Constants.REGION === 'uk';
  const { language } = Constants.REGION_CONFIG;

  const parts = new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: isUk ? 'long' : 'short',
    year: 'numeric',
    hour: isUk ? '2-digit' : 'numeric',
    minute: '2-digit',
    hour12: !isUk,
    userTimeZone,
    timeZoneName: 'short',
  }).formatToParts(date);

  const day = partValue(parts, 'day');
  const month = partValue(parts, 'month');
  const year = partValue(parts, 'year');
  const hour = partValue(parts, 'hour');
  const minute = partValue(parts, 'minute');
  const dayPeriod = partValue(parts, 'dayPeriod');
  const zone = partValue(parts, 'timeZoneName');

  const datePart = isUk ? `${day} ${month} ${year}` : `${month} ${day}, ${year}`;
  const timePart = isUk ? `${hour}:${minute}` : `${hour}:${minute} ${dayPeriod}`;

  return `Session began ${datePart} at ${timePart} ${zone}`;
}

// Short list-item date, e.g. "Wed, Aug 5" (global) / "Wed 5 Aug" (uk)
export function formatTranscriptListDate(dateInput) {
  const date = new Date(dateInput);
  const { language, timeZone } = Constants.REGION_CONFIG;

  return date.toLocaleDateString(language, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  });
}

// Month/year group label, e.g. "August 2026"
export function formatTranscriptMonthLabel(monthKey) {
  const [year, month] = monthKey.split('-');
  const date = new Date(`${year}-${month}-15`);
  const { language } = Constants.REGION_CONFIG;

  return date.toLocaleDateString(language, {
    month: 'long',
    year: 'numeric',
  });
}

// Short date only, e.g. "05/08/2026" (uk) / "08/05/2026" (global)
export function formatTranscriptShortDate(dateInput) {
  const date = new Date(dateInput);
  const { language, timeZone } = Constants.REGION_CONFIG;

  return date.toLocaleDateString(language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone,
  });
}

function formatTime(date, { language, timeZone, hour12 }) {
  return date.toLocaleTimeString(language, { hour: hour12 ? 'numeric' : '2-digit', minute: '2-digit', hour12, timeZone });
}

// Duration in minutes, e.g. "55 min" / "2 hr 15 min"
export function formatDurationLabel(minutes) {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

// Recording offset in seconds as a timecode, e.g. "00:47:12"
export function formatTimecode(seconds) {
  const total = Math.max(0, Math.floor(seconds || 0));
  return [Math.floor(total / 3600), Math.floor(total / 60) % 60, total % 60]
    .map(value => String(value).padStart(2, '0'))
    .join(':');
}

// Sidebar item meta, e.g. "Sep 10 · 55 min" (global) / "10 Sep · 55 min" (uk)
export function formatSessionListMeta(dateInput, minutes) {
  const { language, timeZone } = Constants.REGION_CONFIG;
  const date = new Date(dateInput).toLocaleDateString(language, { month: 'short', day: 'numeric', timeZone });
  return [date, formatDurationLabel(minutes)].filter(Boolean).join(' · ');
}

// Session date, time range and duration, e.g. "Sep 10, 2026 · 9:00–9:55 AM · 55 min" (global)
// / "10 Sep 2026 · 09:00–09:55 · 55 min" (uk)
export function formatSessionRangeLabel(dateInput, minutes) {
  const start = new Date(dateInput);
  if (Number.isNaN(start.getTime())) return '';

  const { language, timeZone } = Constants.REGION_CONFIG;
  const hour12 = Constants.REGION !== 'uk';
  const end = new Date(start.getTime() + (minutes || 0) * 60000);

  const date = start.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric', timeZone });
  const startTime = formatTime(start, { language, timeZone, hour12 });
  const endTime = formatTime(end, { language, timeZone, hour12 });
  // Keep AM/PM once when both ends share it: "9:00–9:55 AM"
  const startPeriod = startTime.match(/\s?[AP]M$/i);
  const range = hour12 && startPeriod && endTime.endsWith(startPeriod[0].trim())
    ? `${startTime.slice(0, -startPeriod[0].length)}–${endTime}`
    : `${startTime}–${endTime}`;

  return [date, range, formatDurationLabel(minutes)].filter(Boolean).join(' · ');
}
